import { useMemo, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Hourly "solar vs usage" explainer for the CSC calculator.
//
// A business week is split into OPERATING days (full load) and NON-OPERATING
// days (a baseline standby load only — no daytime business consumption). The
// user toggles between the two representative days. On non-operating days the
// daytime load disappears, so solar surplus (and export, or the battery needed
// to capture it) is much larger.
//
// Load shape (user-confirmed): on an OPERATING day the total consumption is
// spread FLAT across the operating hours only (uniform within 09:00–17:00, zero
// outside) — a business doesn't draw load overnight. On a NON-OPERATING day
// there's only a small standby baseline spread flat across 24h. Solar is a
// half-sine bell over 08:00–18:00. Self-use = min(solar, load); the rest of
// load is grid import (split TNB day vs night); leftover solar is export.
// Battery estimate = the single largest one-hour surplus (peak-hour model: a
// 16 kWh peak hour needs a 16 kWh battery, not the daily total).

const YIELD_PER_KWP = 3.4;             // kWh per kWp per day (MY commercial)
const SOLAR_START = 8, SOLAR_END = 18, SOLAR_HOURS = SOLAR_END - SOLAR_START;
const BASELINE_SHARE = 0.30;           // non-operating-day load as a share of an operating day
const WORKWEEKS_PER_MONTH = 4.345;     // matches the saving calc in useCscBillProposal
const PP = "'Poppins', sans-serif";

// Approx kWh generated in the hour starting at h (midpoint of a normalised
// half-sine whose whole-day integral = kWp × YIELD_PER_KWP).
function solarKwhAt(kwp, h) {
    const mid = h + 0.5;
    if (mid < SOLAR_START || mid > SOLAR_END) return 0;
    const dailyGen = kwp * YIELD_PER_KWP;
    const peakKw = dailyGen * Math.PI / (2 * SOLAR_HOURS);
    return peakKw * Math.sin(Math.PI * (mid - SOLAR_START) / SOLAR_HOURS);
}

export default function CscEnergyChart({ kwp = 0, monthlyKwh = 0, opStart = 9, opEnd = 17, fullSelfUse = true, operatingDays = 5 }) {
    const [dayType, setDayType] = useState('operating'); // 'operating' | 'nonOperating'

    // Conserve total monthly energy when splitting it into operating-day load
    // D_op and non-operating-day load D_non = BASELINE_SHARE × D_op:
    //   monthlyKwh = workdays·D_op + offdays·(BASELINE_SHARE·D_op)
    const { dailyOp, dailyNon } = useMemo(() => {
        const workdays = Math.max(0, operatingDays) * WORKWEEKS_PER_MONTH;
        const offdays = Math.max(0, 7 - operatingDays) * WORKWEEKS_PER_MONTH;
        const denom = workdays + BASELINE_SHARE * offdays;
        const dOp = denom > 0 ? monthlyKwh / denom : 0;
        return { dailyOp: dOp, dailyNon: BASELINE_SHARE * dOp };
    }, [monthlyKwh, operatingDays]);

    const dailyConsumption = dayType === 'operating' ? dailyOp : dailyNon;

    const { data, peakSurplus } = useMemo(() => {
        const opHours = Math.max(1, opEnd - opStart);
        const opHourly = dailyOp / opHours;   // flat WITHIN operating hours
        const nonHourly = dailyNon / 24;      // standby baseline, flat across 24h
        const r1 = (v) => Math.round(v * 10) / 10;
        const rows = [];
        let peak = 0;
        for (let h = 0; h < 24; h++) {
            const inOp = h >= opStart && h < opEnd;
            const load = dayType === 'operating' ? (inOp ? opHourly : 0) : nonHourly;
            const solar = solarKwhAt(kwp, h);
            const self = Math.min(solar, load);
            const imp = Math.max(0, load - self);
            const surplus = Math.max(0, solar - self);   // raw hourly surplus (battery basis)
            if (surplus > peak) peak = surplus;
            // Non-operating days have (almost) no load, so the surplus is real export
            // regardless of the 100%-self-use toggle — that's the whole point of the view.
            const exp = (dayType === 'nonOperating') ? surplus : (fullSelfUse ? 0 : surplus);
            const isDay = h >= 8 && h < 18;              // TNB day 8am–6pm
            rows.push({
                hour: `${h}:00`,
                dayImport: isDay ? r1(imp) : 0,
                nightImport: isDay ? 0 : r1(imp),
                solar: r1(solar),
                gridExport: r1(exp),
            });
        }
        return { data: rows, peakSurplus: peak };
    }, [kwp, dailyOp, dailyNon, dayType, opStart, opEnd, fullSelfUse]);

    if (!kwp || !monthlyKwh) return null;

    const batteryKwh = Math.ceil(peakSurplus);
    const offDaysPerWeek = Math.max(0, 7 - operatingDays);

    return (
        <Box sx={{ fontFamily: PP }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={dayType}
                    onChange={(e, v) => { if (v) setDayType(v); }}
                    sx={{ '& .MuiToggleButton-root': { fontFamily: PP, textTransform: 'none', fontSize: '0.72rem', px: 1.25, py: 0.4 } }}
                >
                    <ToggleButton value="operating">Operating day ({operatingDays}/week)</ToggleButton>
                    <ToggleButton value="nonOperating">Non-operating day ({offDaysPerWeek}/week)</ToggleButton>
                </ToggleButtonGroup>

                {batteryKwh > 0 && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#0F766E', fontFamily: PP, fontWeight: 600 }}>
                        Battery to capture peak-hour surplus: ~{batteryKwh} kWh
                    </Typography>
                )}
            </Box>

            <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP, mb: 1 }}>
                {dayType === 'operating'
                    ? `Full business load (~${Math.round(dailyConsumption)} kWh/day), spread evenly across operating hours ${opStart}:00–${opEnd}:00 only.`
                    : `Baseline standby only (~${Math.round(dailyConsumption)} kWh/day) — business closed, so almost all solar is exported.`}
            </Typography>

            <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                    <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }} barCategoryGap="12%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#64748B' }} interval={1} />
                        <YAxis tick={{ fontSize: 9, fill: '#64748B' }} width={34}
                            label={{ value: 'kWh / hour', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94A3B8' } }} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, fontFamily: PP }} />
                        <Legend wrapperStyle={{ fontSize: 11, fontFamily: PP }} />
                        <Bar dataKey="dayImport" name="TNB day import (8am–6pm)" fill="#38BDF8" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="nightImport" name="TNB night import (6pm–8am)" fill="#7C3AED" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="solar" name="Solar generated" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="gridExport" name="Export to grid" fill="#22C55E" radius={[2, 2, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Box>
    );
}
